import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import Icon from '../AppIcon';
import Button from './Button';

const SuccessModal = ({
  isOpen = false,
  onClose,
  title = 'Success!',
  message = 'Your action was completed successfully',
  orderDetails = null,
  ctaText = 'Continue',
  ctaIcon = 'ArrowRight',
  onCtaClick
}) => {
  // Trigger confetti animation when modal opens
  useEffect(() => {
    if (isOpen) {
      // Initial burst
      const duration = 3000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

      const randomInRange = (min, max) => Math.random() * (max - min) + min;

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);

        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
        });
      }, 250);

      return () => clearInterval(interval);
    }
  }, [isOpen]);

  const handleCtaClick = () => {
    if (onCtaClick) {
      onCtaClick();
    } else if (onClose) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-[9998] backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="bg-white rounded-3xl border border-gray-200 shadow-2xl p-8 md:p-12 text-center max-w-2xl w-full relative"
              onClick={(e) => e?.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <Icon name="X" size={20} color="#6B7280" />
              </button>

              {/* Decorative Icons */}
              <div className="absolute top-8 left-8 md:top-12 md:left-12 animate-float">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Icon name="Sparkles" size={20} color="#10B981" />
                </div>
              </div>
              <div className="absolute top-8 right-8 md:top-12 md:right-16 animate-float-delayed">
                <Icon name="Star" size={24} color="#FBBF24" />
              </div>

              {/* Main Icon with Gradient Background */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, duration: 0.5, type: 'spring', stiffness: 200 }}
                className="relative inline-block mb-8"
              >
                {/* Outer glow circle */}
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-200 to-cyan-200 rounded-full blur-2xl opacity-40 scale-110"></div>
                {/* Middle circle */}
                <div className="relative w-32 h-32 md:w-40 md:h-40 rounded-full bg-gradient-to-br from-emerald-100 to-cyan-100 flex items-center justify-center">
                  {/* Inner circle with icon */}
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
                    <Icon name="CheckCircle2" size={40} color="#FFFFFF" />
                  </div>
                </div>
              </motion.div>

              {/* Title */}
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
              >
                {title}
              </motion.h2>

              {/* Message */}
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.3 }}
                className="text-base md:text-lg text-gray-500 leading-relaxed mb-6 max-w-lg mx-auto"
              >
                {message}
              </motion.p>

              {/* Order Details Card */}
              {orderDetails && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.3 }}
                  className="bg-gradient-to-br from-purple-50 to-cyan-50 rounded-2xl p-6 mb-8 border border-purple-100"
                >
                  {/* Transaction ID */}
                  {orderDetails?.transactionId && (
                    <div className="mb-4 pb-4 border-b border-purple-200">
                      <div className="text-xs text-gray-500 mb-1">Transaction ID</div>
                      <div className="text-sm font-mono text-gray-700">{orderDetails?.transactionId}</div>
                    </div>
                  )}

                  {/* Package Info */}
                  {orderDetails?.packageInfo && (
                    <div className="flex items-center gap-3 mb-4 pb-4 border-b border-purple-200">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon name="Zap" size={24} color="#ffffff" />
                      </div>
                      <div className="text-left flex-1">
                        <div className="font-bold text-gray-900">{orderDetails?.packageInfo?.name}</div>
                        <div className="text-sm text-gray-600">{orderDetails?.packageInfo?.credits} Credits</div>
                      </div>
                    </div>
                  )}

                  {/* Credit Balance Update */}
                  {orderDetails?.creditsAdded && (
                    <div className="bg-white/80 rounded-xl p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <div className="text-left">
                          <div className="text-xs text-gray-500 mb-1">New Credit Balance</div>
                          <div className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text text-transparent">
                            {orderDetails?.creditsAdded} Credits
                          </div>
                        </div>
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-cyan-100 rounded-full flex items-center justify-center">
                          <Icon name="TrendingUp" size={24} color="#9333EA" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Price Breakdown */}
                  <div className="space-y-2">
                    {orderDetails?.subtotal && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Subtotal</span>
                        <span className="font-medium text-gray-900">${orderDetails?.subtotal?.toFixed(2)}</span>
                      </div>
                    )}
                    {orderDetails?.tax && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Tax</span>
                        <span className="font-medium text-gray-900">${orderDetails?.tax?.toFixed(2)}</span>
                      </div>
                    )}
                    {orderDetails?.total && (
                      <div className="flex items-center justify-between pt-2 border-t border-purple-200">
                        <span className="font-bold text-gray-900">Total Paid</span>
                        <span className="text-xl font-bold text-purple-600">${orderDetails?.total?.toFixed(2)}</span>
                      </div>
                    )}
                  </div>

                  {/* Receipt Email Notice */}
                  {orderDetails?.email && (
                    <div className="mt-4 pt-4 border-t border-purple-200">
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Icon name="Mail" size={14} color="#6B7280" />
                        <span>Receipt sent to {orderDetails?.email}</span>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {/* CTA Button */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.3 }}
              >
                <button
                  onClick={handleCtaClick}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-cyan-500 text-white font-semibold text-lg rounded-2xl hover:opacity-90 transition-opacity shadow-lg hover:shadow-xl transform hover:scale-105 transition-transform duration-200"
                >
                  {ctaText}
                  <Icon name={ctaIcon} size={24} color="#FFFFFF" />
                </button>
              </motion.div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SuccessModal;